import React, { useState } from 'react'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'
import { Button, Card, DataTable, PageHeader, CreateModal, EditModal, FormField, Input } from '@/shared/ui'
import type { RegionResponse } from '@/shared/api'

interface RegionManagerProps {
  title: string
  description: string
  data: RegionResponse[]
  loading: boolean
  error: any
  includeInactive: boolean
  onToggleInactive: (value: boolean) => void
  onCreate: (data: RegionCreateData) => void
  onUpdate: (id: number, data: RegionUpdateData) => void
  onDelete: (id: number) => void
  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

interface RegionCreateData {
  name: string
  full_name?: string | undefined
  fias?: string | undefined
  longitude?: number | undefined
  latitude?: number | undefined
  kladr?: string | undefined
}

interface RegionUpdateData {
  name?: string | undefined
  full_name?: string | undefined
  fias?: string | undefined
  longitude?: number | undefined
  latitude?: number | undefined
  kladr?: string | undefined
  is_active?: boolean | undefined
}

interface CreateFormData {
  name: string
  full_name: string
  fias: string
  longitude: string
  latitude: string
  kladr: string
}

interface EditFormData {
  id: number
  name: string
  full_name: string
  fias: string
  longitude: string
  latitude: string
  kladr: string
  isActive: boolean
}

export const RegionManager: React.FC<RegionManagerProps> = ({
  title,
  description,
  data = [],
  loading = false,
  error,
  includeInactive = false,
  onToggleInactive,
  onCreate,
  onUpdate,
  onDelete,
  isCreating = false,
  isUpdating = false,
  isDeleting = false
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingItem, setEditingItem] = useState<EditFormData | null>(null)
  const [createForm, setCreateForm] = useState<CreateFormData>({
    name: '',
    full_name: '',
    fias: '',
    longitude: '',
    latitude: '',
    kladr: ''
  })

  const handleCreateSubmit = () => {
    if (!createForm.name.trim()) return

    const data: RegionCreateData = {
      name: createForm.name.trim(),
      full_name: createForm.full_name.trim() || undefined,
      fias: createForm.fias.trim() || undefined,
      longitude: createForm.longitude ? parseFloat(createForm.longitude) : undefined,
      latitude: createForm.latitude ? parseFloat(createForm.latitude) : undefined,
      kladr: createForm.kladr.trim() || undefined
    }

    onCreate(data)
    setCreateForm({
      name: '',
      full_name: '',
      fias: '',
      longitude: '',
      latitude: '',
      kladr: ''
    })
    setShowCreateForm(false)
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingItem || !editingItem.name.trim()) return

    const data: RegionUpdateData = {
      name: editingItem.name.trim(),
      full_name: editingItem.full_name.trim() || undefined,
      fias: editingItem.fias.trim() || undefined,
      longitude: editingItem.longitude ? parseFloat(editingItem.longitude) : undefined,
      latitude: editingItem.latitude ? parseFloat(editingItem.latitude) : undefined,
      kladr: editingItem.kladr.trim() || undefined,
      is_active: editingItem.isActive
    }

    onUpdate(editingItem.id, data)
    setEditingItem(null)
  }

  const startEdit = (item: RegionResponse) => {
    if (!item?.id) return
    
    setEditingItem({
      id: item.id,
      name: item.name || '',
      full_name: item.full_name || '',
      fias: item.fias || '',
      longitude: item.longitude?.toString() || '',
      latitude: item.latitude?.toString() || '',
      kladr: item.kladr || '',
      isActive: Boolean(item.is_active)
    })
  }

  const columns = [
    {
      key: 'name' as keyof RegionResponse,
      title: 'Название',
      render: (_: any, item: RegionResponse) => item?.name || '—'
    },
    {
      key: 'full_name' as keyof RegionResponse,
      title: 'Полное название',
      render: (_: any, item: RegionResponse) => item?.full_name || '—'
    },
    {
      key: 'fias' as keyof RegionResponse,
      title: 'ФИАС',
      render: (_: any, item: RegionResponse) => item?.fias || '—'
    },
    {
      key: 'longitude' as keyof RegionResponse,
      title: 'Координаты',
      render: (_: any, item: RegionResponse) => {
        if (item?.longitude && item?.latitude) {
          return `${item.latitude}, ${item.longitude}`
        }
        return '—'
      }
    },
    {
      key: 'kladr' as keyof RegionResponse,
      title: 'КЛАДР',
      render: (_: any, item: RegionResponse) => item?.kladr || '—'
    },
    {
      key: 'is_active' as keyof RegionResponse,
      title: 'Статус',
      render: (_: any, item: RegionResponse) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          Boolean(item?.is_active)
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {Boolean(item?.is_active) ? 'Активен' : 'Неактивен'}
        </span>
      )
    },
    {
      key: 'actions' as const,
      title: 'Действия',
      render: (_: any, item: RegionResponse) => {
        if (!item?.id) return null
        
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => startEdit(item)}
              disabled={isUpdating}
            >
              <PencilIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(item.id)}
              disabled={isDeleting}
              className="text-red-600 hover:text-red-700"
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          </div>
        )
      }
    }
  ]

  if (error) {
    return (
      <div>
        <PageHeader title={title} description={description} />
        <Card>
          <div className="p-6">
            <div className="text-red-600">
              Ошибка загрузки данных: {error?.message || 'Неизвестная ошибка'}
            </div>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <>
      <PageHeader title={title} description={description}>
        <Button
          onClick={() => setShowCreateForm(true)}
          disabled={isCreating}
        >
          {isCreating ? 'Создание...' : 'Создать'}
        </Button>
      </PageHeader>

      <div className="flex-1 p-6 overflow-auto">
        {/* Форма создания */}
      <CreateModal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Создать новый регион"
        fields={[
          {
            name: 'name',
            label: 'Название',
            type: 'text',
            required: true,
            placeholder: 'Введите название региона',
            value: createForm.name,
            onChange: (value) => setCreateForm({ ...createForm, name: value })
          },
          {
            name: 'full_name',
            label: 'Полное название',
            type: 'text',
            placeholder: 'Введите полное название',
            value: createForm.full_name,
            onChange: (value) => setCreateForm({ ...createForm, full_name: value })
          },
          {
            name: 'fias',
            label: 'ФИАС код',
            type: 'text',
            placeholder: 'Введите ФИАС код',
            value: createForm.fias,
            onChange: (value) => setCreateForm({ ...createForm, fias: value })
          },
          {
            name: 'kladr',
            label: 'КЛАДР код',
            type: 'text',
            placeholder: 'Введите КЛАДР код',
            value: createForm.kladr,
            onChange: (value) => setCreateForm({ ...createForm, kladr: value })
          },
          {
            name: 'latitude',
            label: 'Широта',
            type: 'text',
            placeholder: 'Например: 55.7558',
            value: createForm.latitude,
            onChange: (value) => setCreateForm({ ...createForm, latitude: value })
          },
          {
            name: 'longitude',
            label: 'Долгота',
            type: 'text',
            placeholder: 'Например: 37.6173',
            value: createForm.longitude,
            onChange: (value) => setCreateForm({ ...createForm, longitude: value })
          }
        ]}
        onSubmit={handleCreateSubmit}
        onCancel={() => {
          setCreateForm({ name: '', full_name: '', fias: '', kladr: '', latitude: '', longitude: '' })
        }}
        isLoading={isCreating}
      />

      {/* Модальное окно редактирования */}
      <EditModal
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        title="Редактировать регион"
        fields={[
          {
            name: 'name',
            label: 'Название',
            type: 'text',
            required: true,
            placeholder: 'Введите название региона',
            value: editingItem?.name || '',
            onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, name: value }) : null)
          },
          {
            name: 'full_name',
            label: 'Полное название',
            type: 'text',
            placeholder: 'Введите полное название',
            value: editingItem?.full_name || '',
            onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, full_name: value }) : null)
          },
          {
            name: 'fias',
            label: 'ФИАС код',
            type: 'text',
            placeholder: 'Введите ФИАС код',
            value: editingItem?.fias || '',
            onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, fias: value }) : null)
          },
          {
            name: 'kladr',
            label: 'КЛАДР код',
            type: 'text',
            placeholder: 'Введите КЛАДР код',
            value: editingItem?.kladr || '',
            onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, kladr: value }) : null)
          },
          {
            name: 'latitude',
            label: 'Широта',
            type: 'text',
            placeholder: 'Например: 55.7558',
            value: editingItem?.latitude || '',
            onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, latitude: value }) : null)
          },
          {
            name: 'longitude',
            label: 'Долгота',
            type: 'text',
            placeholder: 'Например: 37.6173',
            value: editingItem?.longitude || '',
            onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, longitude: value }) : null)
          },
          {
            name: 'isActive',
            label: 'Активен',
            type: 'checkbox',
            value: editingItem?.isActive || false,
            onChange: (value) => setEditingItem(prev => prev ? ({ ...prev, isActive: value }) : null)
          }
        ]}
        onSubmit={() => {
          if (editingItem && editingItem.name.trim()) {
            const data: RegionUpdateData = {
              name: editingItem.name.trim(),
              full_name: editingItem.full_name.trim() || undefined,
              fias: editingItem.fias.trim() || undefined,
              longitude: editingItem.longitude ? parseFloat(editingItem.longitude) : undefined,
              latitude: editingItem.latitude ? parseFloat(editingItem.latitude) : undefined,
              kladr: editingItem.kladr.trim() || undefined,
              is_active: editingItem.isActive
            }
            onUpdate(editingItem.id, data)
            setEditingItem(null)
          }
        }}
        onCancel={() => setEditingItem(null)}
        isLoading={isUpdating}
      />

      {/* Таблица данных */}
      <Card>
        <DataTable
          data={data || []}
          columns={columns}
          loading={loading}
          emptyMessage="Регионы не найдены"
        />
      </Card>
      </div>
    </>
  )
}
